import { useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { calcSizeForTv } from 'utils/index';

import { KeyboardAppearanceType } from './SimpleKeyboard';
import { KeyItemProps, getAlphabetKeyboardUppercase } from './helpers/keyboardItems';

type AlphabetKeyboardProps = {
  onHandleKeyPress: (keyItem: KeyItemProps) => void;
  appearance?: KeyboardAppearanceType;
};

const ITEM_SIZE = 52;
const GAP = 8;
const TOTAL_COLUMNS = 6;

const AlphabetKeyboard: React.FC<AlphabetKeyboardProps> = ({
  onHandleKeyPress,
  appearance = 'default',
}) => {
  const [uppercase, setUppercase] = useState(false);

  const keyboard = useMemo(() => getAlphabetKeyboardUppercase(uppercase), [uppercase]);

  const backgroundColor =
    appearance === 'strong' ? 'rgba(6, 113, 224, 0.15)' : 'rgba(6, 113, 224, 0.07)';

  const getItemWidth = (columns?: number) => {
    const col = columns ?? 1;
    return calcSizeForTv(ITEM_SIZE * col + GAP * (col - 1));
  };

  const handlePress = (keyItem: KeyItemProps) => {
    if (keyItem.item === 'shift') {
      setUppercase((prev) => !prev);
      return;
    }

    onHandleKeyPress(keyItem);
  };

  return (
    <View style={styles.container}>
      {keyboard.map((keyItem, index) => (
        <View
          key={`${keyItem.item}-${index}`}
          style={{
            width: getItemWidth(keyItem.columns),
            marginRight: calcSizeForTv(GAP),
            marginBottom: calcSizeForTv(GAP),
          }}>
          <Pressable
            role="button"
            style={[
              styles.keyButton,
              {
                backgroundColor:
                  keyItem.item === 'shift' && uppercase
                    ? 'rgba(6, 113, 224, 0.35)'
                    : backgroundColor,
                height: calcSizeForTv(ITEM_SIZE),
                justifyContent: 'center',
                alignItems: 'center',
              },
            ]}
            onPress={() => handlePress(keyItem)}>
            <Text style={styles.keyText}>{keyItem.symbol}</Text>
          </Pressable>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: calcSizeForTv(ITEM_SIZE * TOTAL_COLUMNS + GAP * TOTAL_COLUMNS),
  },
  keyButton: {
    paddingHorizontal: 0,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 2,
  },
  keyText: {
    fontSize: calcSizeForTv(20),
    color: '#FFF',
  },
});

export default AlphabetKeyboard;
